import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Download, Copy, KeyRound, ChevronRight, ArrowUpDown, Check, X, RefreshCw, Eye,
} from "lucide-react";
import { api } from "../../lib/api";

const fmtDate = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};
const isPaid = (c) => (Array.isArray(c.entitlements) && c.entitlements.length > 0) || !!c.purchased_at;
const isActive = (c) => c.last_active && Date.now() - new Date(c.last_active).getTime() < 7 * 864e5;
const csvCell = (v) => {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

function StatCard({ label, value, accent }) {
  return (
    <div className="bps-card p-4">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#7C8FA8]">{label}</div>
      <div className="font-display text-3xl font-semibold mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}

const SortBtn = ({ label, k, sortKey, sortDir, onSort, className = "" }) => (
  <button onClick={() => onSort(k)} className={`inline-flex items-center gap-1 hover:text-white transition-colors ${sortKey === k ? "text-white" : ""} ${className}`} data-testid={`sort-${k}`}>
    {label}
    <ArrowUpDown size={11} className={sortKey === k ? "text-[#22D3EE]" : "text-[#4F627B]"} />
  </button>
);

export default function CustomersPanel({ clients }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [pay, setPay] = useState("all"); // all | paid | free
  const [active, setActive] = useState("all"); // all | active | inactive
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [copied, setCopied] = useState(null);
  const [pwTarget, setPwTarget] = useState(null);

  const onSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir(k === "name" || k === "email" ? "asc" : "desc"); }
  };

  const rows = useMemo(() => {
    let r = [...(clients || [])];
    const q = query.trim().toLowerCase();
    if (q) r = r.filter((c) => (c.name || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q));
    if (pay !== "all") r = r.filter((c) => (pay === "paid" ? isPaid(c) : !isPaid(c)));
    if (active !== "all") r = r.filter((c) => (active === "active" ? isActive(c) : !isActive(c)));
    r.sort((a, b) => {
      let va, vb;
      switch (sortKey) {
        case "name": va = (a.name || "").toLowerCase(); vb = (b.name || "").toLowerCase(); break;
        case "email": va = (a.email || "").toLowerCase(); vb = (b.email || "").toLowerCase(); break;
        case "last_active": va = a.last_active ? new Date(a.last_active).getTime() : 0; vb = b.last_active ? new Date(b.last_active).getTime() : 0; break;
        case "progress": va = a.steps_done || 0; vb = b.steps_done || 0; break;
        default: va = a.created_at ? new Date(a.created_at).getTime() : 0; vb = b.created_at ? new Date(b.created_at).getTime() : 0;
      }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return r;
  }, [clients, query, pay, active, sortKey, sortDir]);

  const copyEmail = (email) => {
    navigator.clipboard?.writeText(email).then(() => {
      setCopied(email);
      setTimeout(() => setCopied(null), 1500);
    }).catch(() => {});
  };

  const exportCSV = () => {
    const header = ["Name", "Email", "Joined", "Status", "Last Active", "Weeks Done", "Days Done", "Last Pain"];
    const lines = rows.map((c) => [
      c.name, c.email, fmtDate(c.created_at), isPaid(c) ? "Paid" : "Free",
      c.last_active ? fmtDate(c.last_active) : "", `${c.steps_done || 0}/4`,
      `${c.days_completed || 0}/${c.days_total || 28}`, c.last_pain ?? "",
    ].map(csvCell).join(","));
    const csv = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `customers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const total = (clients || []).length;
  const paidCount = (clients || []).filter(isPaid).length;
  const activeCount = (clients || []).filter(isActive).length;

  return (
    <div data-testid="customers-panel">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <StatCard label="Total Customers" value={total} accent="#FFFFFF" />
        <StatCard label="Paid" value={paidCount} accent="#10B981" />
        <StatCard label="Active (7d)" value={activeCount} accent="#22D3EE" />
        <StatCard label="Showing" value={rows.length} accent="#F59E0B" />
      </div>

      {/* Toolbar */}
      <div className="bps-card p-4 mb-4 flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7C8FA8]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or email…"
            data-testid="customer-search"
            className="w-full bg-white/5 rounded-full pl-9 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#22D3EE]"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <FilterGroup value={pay} onChange={setPay} options={[["all", "All"], ["paid", "Paid"], ["free", "Free"]]} testid="filter-pay" />
          <FilterGroup value={active} onChange={setActive} options={[["all", "Any"], ["active", "Active"], ["inactive", "Inactive"]]} testid="filter-active" />
          <button onClick={exportCSV} data-testid="export-csv" className="inline-flex items-center gap-1.5 text-sm px-4 py-2.5 rounded-full bg-[#22D3EE] text-[#03101F] font-medium">
            <Download size={14} /> CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bps-card overflow-x-auto" data-testid="customers-table">
        <table className="w-full min-w-[760px] text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-[0.12em] text-[#7C8FA8] border-b border-white/10">
              <th className="text-left font-medium p-4"><SortBtn label="Customer" k="name" {...{ sortKey, sortDir, onSort }} /></th>
              <th className="text-left font-medium p-4"><SortBtn label="Email (login)" k="email" {...{ sortKey, sortDir, onSort }} /></th>
              <th className="text-left font-medium p-4"><SortBtn label="Joined" k="created_at" {...{ sortKey, sortDir, onSort }} /></th>
              <th className="text-left font-medium p-4">Status</th>
              <th className="text-left font-medium p-4"><SortBtn label="Last active" k="last_active" {...{ sortKey, sortDir, onSort }} /></th>
              <th className="text-left font-medium p-4"><SortBtn label="Progress" k="progress" {...{ sortKey, sortDir, onSort }} /></th>
              <th className="text-right font-medium p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-[#9AAEC6]">No customers match your filters.</td></tr>
            )}
            {rows.map((c) => (
              <tr key={c.id} className="border-b border-white/[0.05] last:border-0 hover:bg-white/[0.02]" data-testid={`customer-row-${c.id}`}>
                <td className="p-4">
                  <div className="font-medium text-white">{c.name || "—"}</div>
                  {isActive(c) && <span className="text-[10px] text-[#22D3EE]">● online recently</span>}
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <span className="text-[#C7D6EA]">{c.email}</span>
                    <button onClick={() => copyEmail(c.email)} title="Copy email" data-testid={`copy-email-${c.id}`} className="text-[#7C8FA8] hover:text-[#22D3EE]">
                      {copied === c.email ? <Check size={13} className="text-[#10B981]" /> : <Copy size={13} />}
                    </button>
                  </div>
                </td>
                <td className="p-4 text-[#9AAEC6]">{fmtDate(c.created_at)}</td>
                <td className="p-4">
                  {isPaid(c)
                    ? <span className="text-[11px] px-2.5 py-1 rounded-full bg-[#10B981]/15 text-[#34D399] border border-[#10B981]/25">Paid</span>
                    : <span className="text-[11px] px-2.5 py-1 rounded-full bg-white/5 text-[#9AAEC6] border border-white/10">Free</span>}
                </td>
                <td className="p-4 text-[#9AAEC6]">{c.last_active ? fmtDate(c.last_active) : "—"}</td>
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-[#22D3EE]" style={{ width: `${((c.steps_done || 0) / 4) * 100}%` }} />
                    </div>
                    <span className="text-xs text-[#9AAEC6]">{c.steps_done || 0}/4</span>
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => setPwTarget(c)} title="Set / reset password" data-testid={`set-password-${c.id}`} className="p-2 rounded-lg text-[#9AAEC6] hover:text-[#F59E0B] hover:bg-white/5">
                      <KeyRound size={15} />
                    </button>
                    <button onClick={() => navigate(`/admin/clients/${c.id}`)} title="View details" data-testid={`view-customer-${c.id}`} className="p-2 rounded-lg text-[#9AAEC6] hover:text-[#22D3EE] hover:bg-white/5">
                      <Eye size={15} />
                    </button>
                    <ChevronRight size={15} className="text-[#4F627B]" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pwTarget && <SetPasswordModal customer={pwTarget} onClose={() => setPwTarget(null)} />}
    </div>
  );
}

function FilterGroup({ value, onChange, options, testid }) {
  return (
    <div className="flex bg-white/5 rounded-full p-0.5" data-testid={testid}>
      {options.map(([v, label]) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          className={`px-3 py-2 rounded-full text-xs font-medium transition-colors ${value === v ? "bg-[#22D3EE] text-[#03101F]" : "text-[#9AAEC6] hover:text-white"}`}
          data-testid={`${testid}-${v}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function SetPasswordModal({ customer, onClose }) {
  const [password, setPassword] = useState("");
  const [generate, setGenerate] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  async function submit() {
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post(`/admin/clients/${customer.id}/set-password`, {
        generate, password: generate ? undefined : password,
      });
      setResult(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not set password.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="set-password-modal">
      <div className="bps-card bps-card-accent w-full max-w-md p-7" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="bps-eyebrow text-[#F59E0B]">Set / Reset Password</div>
            <h3 className="font-display text-xl font-semibold text-white mt-1.5">{customer.name}</h3>
            <p className="text-xs text-[#9AAEC6] mt-0.5">{customer.email}</p>
          </div>
          <button onClick={onClose} className="text-[#7C8FA8] hover:text-white"><X size={18} /></button>
        </div>

        {!result ? (
          <>
            <div className="mt-5 flex gap-2">
              <button onClick={() => setGenerate(true)} data-testid="pw-mode-generate" className={`flex-1 py-2.5 rounded-xl text-sm font-medium inline-flex items-center justify-center gap-1.5 ${generate ? "bg-[#22D3EE] text-[#03101F]" : "bg-white/5 text-white"}`}>
                <RefreshCw size={14} /> Auto-generate
              </button>
              <button onClick={() => setGenerate(false)} data-testid="pw-mode-manual" className={`flex-1 py-2.5 rounded-xl text-sm font-medium ${!generate ? "bg-[#22D3EE] text-[#03101F]" : "bg-white/5 text-white"}`}>
                Type my own
              </button>
            </div>

            {!generate && (
              <input
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="New password (min 6 characters)"
                data-testid="pw-input"
                className="w-full bg-white/5 rounded-xl px-4 py-3 mt-3 text-sm outline-none focus:ring-2 focus:ring-[#22D3EE]"
              />
            )}

            <p className="text-xs text-[#7C8FA8] mt-3">
              This sets a new password for the customer and unlocks their account immediately. Share it with them so they can log in and change it in Settings.
            </p>
            {err && <div className="text-sm text-[#F87171] mt-3" data-testid="pw-error">{err}</div>}

            <button
              onClick={submit}
              disabled={loading || (!generate && password.length < 6)}
              data-testid="pw-submit"
              className="w-full mt-5 py-3 rounded-full bg-[#F59E0B] text-[#03101F] font-semibold disabled:opacity-30 transition-opacity"
            >
              {loading ? "Setting…" : "Set Password"}
            </button>
          </>
        ) : (
          <div className="mt-5" data-testid="pw-result">
            <div className="rounded-2xl bg-[#10B981]/10 border border-[#10B981]/25 p-4">
              <div className="bps-eyebrow text-[#10B981] mb-2">New login credentials</div>
              <div className="text-sm text-[#C7D6EA]">Email: <span className="text-white font-medium">{result.email}</span></div>
              <div className="flex items-center gap-2 mt-2">
                <code className="flex-1 bg-black/30 rounded-lg px-3 py-2 text-[#22D3EE] font-mono text-sm break-all" data-testid="pw-result-value">{result.password}</code>
                <button
                  onClick={() => { navigator.clipboard?.writeText(result.password); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                  data-testid="pw-copy"
                  className="shrink-0 p-2.5 rounded-lg bg-white/5 text-white hover:bg-white/10"
                >
                  {copied ? <Check size={15} className="text-[#10B981]" /> : <Copy size={15} />}
                </button>
              </div>
            </div>
            <p className="text-xs text-[#7C8FA8] mt-3">⚠️ Copy this now — for security it won't be shown again.</p>
            <button onClick={onClose} data-testid="pw-done" className="w-full mt-5 py-3 rounded-full bg-[#22D3EE] text-[#03101F] font-semibold">Done</button>
          </div>
        )}
      </div>
    </div>
  );
}
